import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Paper,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BarChartIcon from '@mui/icons-material/BarChart';
import ReactECharts from 'echarts-for-react';
import { formatDisplayDate } from '../../utils/dateUtils';

// Types
export type ValueType = 'Percentage' | 'Amount' | 'Ratio' | 'UOM' | 'Ratings' | string | null;
export type MetricsType = 'Positive' | 'Negative' | 'Target' | string | null;

export const TARGET_LINE_COLOR = '#faad14';
export const THRESHOLD_RED = '#ff4d4f';
export const THRESHOLD_GREEN = '#52c41a';
export const THRESHOLD_RED_SOFT = '#ff7875';
export const THRESHOLD_GREEN_SOFT = '#95de64';

/**
 * Bound line colors by metrics_type:
 * - Positive / Target: lower=red (bad), upper=green (good)
 * - Negative: lower=green (good), upper=red (bad)
 */
export const getThresholdBoundColors = (metricsType?: MetricsType, soft = false) => {
  const isNegative = (metricsType || '').toLowerCase() === 'negative';
  const red = soft ? THRESHOLD_RED_SOFT : THRESHOLD_RED;
  const green = soft ? THRESHOLD_GREEN_SOFT : THRESHOLD_GREEN;
  return {
    lower: isNegative ? green : red,
    upper: isNegative ? red : green,
  };
};

export interface MetricDataPoint {
  metric_date: string;
  metric_value: number;
  metric_name?: string;
  metric_identifier_name?: string | null;
  is_threshold_violated?: boolean;
  lower_bound?: number | null;
  upper_bound?: number | null;
  org_lower_bound?: number | null;
  org_upper_bound?: number | null;
  entity_lower_bound?: number | null;
  entity_upper_bound?: number | null;
  is_threshold_overridden?: boolean;
  target_value?: number | null;
  metrics_type?: MetricsType;
  threshold_direction?: MetricsType;
}

export interface MetricsChartProps {
  // Data
  data: MetricDataPoint[];
  metricName: string;
  
  // Optional metadata
  reviewEntityName?: string;
  metricType?: string;
  metricIdentifierName?: string | null;
  valueType?: ValueType;
  unitOfMeasure?: string | null;
  metricsType?: MetricsType;
  
  // Thresholds (can be passed directly or extracted from data)
  lowerBound?: number | null;
  upperBound?: number | null;
  orgLowerBound?: number | null;
  orgUpperBound?: number | null;
  entityLowerBound?: number | null;
  entityUpperBound?: number | null;
  isThresholdOverridden?: boolean;
  targetValue?: number | null;
  
  // Display options
  showChartTypeToggle?: boolean;
  defaultChartType?: 'line' | 'bar';
  showThresholds?: boolean;
  showSummary?: boolean;
  showMetadata?: boolean;
  height?: number | string;
  
  // Aggregation info
  isAggregate?: boolean;
  aggregateLabel?: string;
}

// Value formatting helper
export const formatMetricValue = (
  value: number | null | undefined,
  valueType?: ValueType
): string => {
  if (value === null || value === undefined) return '-';
  const v = Number(value);
  if (isNaN(v)) return '-';

  switch (valueType?.toLowerCase()) {
    case 'percentage':
      // For percentages stored as decimals (0-1)
      if (Math.abs(v) <= 1 && Math.abs(v) > 0) {
        return `${(v * 100).toFixed(2)}%`;
      }
      return `${v.toFixed(2)}%`;
    case 'amount':
      if (Math.abs(v) >= 1000000) {
        return `₹${(v / 1000000).toFixed(2)}M`;
      } else if (Math.abs(v) >= 1000) {
        return `₹${(v / 1000).toFixed(2)}K`;
      }
      return `₹${v.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    case 'ratings':
      return `${v.toFixed(2)} / 5`;
    case 'ratio':
    case 'uom':
    default:
      if (Math.abs(v) >= 1000000) {
        return `${(v / 1000000).toFixed(2)}M`;
      } else if (Math.abs(v) >= 1000) {
        return `${(v / 1000).toFixed(2)}K`;
      }
      return v.toFixed(2);
  }
};

// Get Y-axis label based on value type
const getYAxisLabel = (valueType?: ValueType): string => {
  switch (valueType?.toLowerCase()) {
    case 'percentage':
      return 'Percentage (%)';
    case 'ratio':
      return 'Ratio';
    case 'amount':
      return 'Amount (₹)';
    case 'uom':
      return 'Units';
    default:
      return 'Value';
  }
};

const MetricsChart: React.FC<MetricsChartProps> = ({
  data,
  metricName,
  reviewEntityName,
  metricType,
  metricIdentifierName,
  valueType,
  unitOfMeasure,
  metricsType,
  lowerBound,
  upperBound,
  orgLowerBound,
  orgUpperBound,
  entityLowerBound,
  entityUpperBound,
  isThresholdOverridden,
  targetValue,
  showChartTypeToggle = true,
  defaultChartType = 'line',
  showThresholds = true,
  showSummary = true,
  showMetadata = true,
  height = 400,
  isAggregate = false,
  aggregateLabel,
}) => {
  const [chartType, setChartType] = useState<'line' | 'bar'>(defaultChartType);

  // Sort data by date
  const sortedData = useMemo(() => {
    return [...data].sort(
      (a, b) => new Date(a.metric_date).getTime() - new Date(b.metric_date).getTime()
    );
  }, [data]);

  // Extract threshold bounds from data if not provided directly
  const thresholds = useMemo(() => {
    // Use provided values first, then try to extract from data
    const dataWithBounds = sortedData.find(
      (d) =>
        d.lower_bound !== null ||
        d.upper_bound !== null ||
        d.org_lower_bound !== null ||
        d.org_upper_bound !== null ||
        d.target_value !== null ||
        d.metrics_type != null ||
        d.threshold_direction != null
    );

    return {
      lower: lowerBound ?? dataWithBounds?.lower_bound ?? null,
      upper: upperBound ?? dataWithBounds?.upper_bound ?? null,
      orgLower: orgLowerBound ?? dataWithBounds?.org_lower_bound ?? null,
      orgUpper: orgUpperBound ?? dataWithBounds?.org_upper_bound ?? null,
      entityLower: entityLowerBound ?? dataWithBounds?.entity_lower_bound ?? null,
      entityUpper: entityUpperBound ?? dataWithBounds?.entity_upper_bound ?? null,
      isOverridden: isThresholdOverridden ?? dataWithBounds?.is_threshold_overridden ?? false,
      target: targetValue ?? dataWithBounds?.target_value ?? null,
      metricsType:
        metricsType ??
        dataWithBounds?.metrics_type ??
        dataWithBounds?.threshold_direction ??
        null,
    };
  }, [
    sortedData,
    lowerBound,
    upperBound,
    orgLowerBound,
    orgUpperBound,
    entityLowerBound,
    entityUpperBound,
    isThresholdOverridden,
    targetValue,
    metricsType,
  ]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (sortedData.length === 0) return null;
    const values = sortedData.map((d) => d.metric_value);
    const violations = sortedData.filter((d) => d.is_threshold_violated).length;
    return {
      latest: values[values.length - 1],
      min: Math.min(...values),
      max: Math.max(...values),
      avg: values.reduce((sum, v) => sum + v, 0) / values.length,
      violations,
      total: values.length,
    };
  }, [sortedData]);

  // Build chart options
  const chartOption = useMemo(() => {
    // Show dates using the user's display_date_format from JWT (not raw YYYY-MM-DD).
    const dates = sortedData.map((d) => formatDisplayDate(d.metric_date));
    const values = sortedData.map((d) => d.metric_value);
    const violations = sortedData.map((d) => d.is_threshold_violated ?? false);
    const boundColors = getThresholdBoundColors(thresholds.metricsType);
    const softBoundColors = getThresholdBoundColors(thresholds.metricsType, true);

    // Calculate Y-axis range including thresholds
    let minValue = values.length > 0 ? Math.min(...values) : 0;
    let maxValue = values.length > 0 ? Math.max(...values) : 0;

    const allBounds: number[] = [];
    if (thresholds.lower !== null) allBounds.push(thresholds.lower);
    if (thresholds.upper !== null) allBounds.push(thresholds.upper);
    if (thresholds.orgLower !== null) allBounds.push(thresholds.orgLower);
    if (thresholds.orgUpper !== null) allBounds.push(thresholds.orgUpper);
    if (thresholds.entityLower !== null) allBounds.push(thresholds.entityLower);
    if (thresholds.entityUpper !== null) allBounds.push(thresholds.entityUpper);
    if (thresholds.target !== null) allBounds.push(thresholds.target);

    if (allBounds.length > 0) {
      minValue = Math.min(minValue, ...allBounds);
      maxValue = Math.max(maxValue, ...allBounds);
    }

    const valueRange = maxValue - minValue;

    // Build threshold mark lines with prominent styling
    const markLineData: any[] = [];

    if (showThresholds) {
      // Simple lower/upper bounds (fallback)
      if (thresholds.lower !== null && thresholds.orgLower === null) {
        markLineData.push({
          yAxis: thresholds.lower,
          name: 'Lower Bound',
          lineStyle: { color: boundColors.lower, type: 'solid', width: 2 },
          label: {
            formatter: `Lower: ${formatMetricValue(thresholds.lower, valueType)}`,
            position: 'insideEndTop',
            fontSize: 11,
            color: boundColors.lower,
            fontWeight: 'bold',
          },
        });
      }
      if (thresholds.upper !== null && thresholds.orgUpper === null) {
        markLineData.push({
          yAxis: thresholds.upper,
          name: 'Upper Bound',
          lineStyle: { color: boundColors.upper, type: 'solid', width: 2 },
          label: {
            formatter: `Upper: ${formatMetricValue(thresholds.upper, valueType)}`,
            position: 'insideEndTop',
            fontSize: 11,
            color: boundColors.upper,
            fontWeight: 'bold',
          },
        });
      }

      // Org-level thresholds (prominent solid lines)
      if (thresholds.orgLower !== null) {
        markLineData.push({
          yAxis: thresholds.orgLower,
          name: 'Org Lower',
          lineStyle: { color: boundColors.lower, type: 'solid', width: 2.5 },
          label: {
            formatter: `Org Lower: ${formatMetricValue(thresholds.orgLower, valueType)}`,
            position: 'insideEndTop',
            fontSize: 11,
            color: boundColors.lower,
            fontWeight: 'bold',
          },
        });
      }
      if (thresholds.orgUpper !== null) {
        markLineData.push({
          yAxis: thresholds.orgUpper,
          name: 'Org Upper',
          lineStyle: { color: boundColors.upper, type: 'solid', width: 2.5 },
          label: {
            formatter: `Org Upper: ${formatMetricValue(thresholds.orgUpper, valueType)}`,
            position: 'insideEndTop',
            fontSize: 11,
            color: boundColors.upper,
            fontWeight: 'bold',
          },
        });
      }

      // Entity-level threshold overrides (dashed lines, only if different)
      if (thresholds.isOverridden) {
        if (
          thresholds.entityLower !== null &&
          thresholds.entityLower !== thresholds.orgLower
        ) {
          markLineData.push({
            yAxis: thresholds.entityLower,
            name: 'Entity Lower',
            lineStyle: { color: softBoundColors.lower, type: 'dashed', width: 2 },
            label: {
              formatter: `Entity Lower: ${formatMetricValue(thresholds.entityLower, valueType)}`,
              position: 'insideEndBottom',
              fontSize: 10,
              color: softBoundColors.lower,
              fontWeight: 'bold',
            },
          });
        }
        if (
          thresholds.entityUpper !== null &&
          thresholds.entityUpper !== thresholds.orgUpper
        ) {
          markLineData.push({
            yAxis: thresholds.entityUpper,
            name: 'Entity Upper',
            lineStyle: { color: softBoundColors.upper, type: 'dashed', width: 2 },
            label: {
              formatter: `Entity Upper: ${formatMetricValue(thresholds.entityUpper, valueType)}`,
              position: 'insideEndBottom',
              fontSize: 10,
              color: softBoundColors.upper,
              fontWeight: 'bold',
            },
          });
        }
      }

      // Target value — prominent yellow line
      if (thresholds.target !== null) {
        markLineData.push({
          yAxis: thresholds.target,
          name: 'Target',
          lineStyle: { color: TARGET_LINE_COLOR, type: 'solid', width: 3 },
          label: {
            formatter: `Target: ${formatMetricValue(thresholds.target, valueType)}`,
            position: 'insideStartTop',
            fontSize: 12,
            color: TARGET_LINE_COLOR,
            fontWeight: 'bold',
          },
        });
      }
    }

    const title = aggregateLabel
      ? `${metricName} ${aggregateLabel}`
      : metricName;

    return {
      title: {
        text: title,
        left: 'center',
        textStyle: { fontSize: 16, fontWeight: 'bold' },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        formatter: (params: any) => {
          const dataIndex = params[0]?.dataIndex;
          if (dataIndex === undefined) return '';
          const date = dates[dataIndex];
          const value = values[dataIndex];
          const violated = violations[dataIndex];

          let thresholdText = '';
          if (thresholds.lower !== null || thresholds.upper !== null) {
            const lowerStr =
              thresholds.lower !== null
                ? formatMetricValue(thresholds.lower, valueType)
                : '-';
            const upperStr =
              thresholds.upper !== null
                ? formatMetricValue(thresholds.upper, valueType)
                : '-';
            thresholdText = `<div style="color: #666; font-size: 11px; margin-top: 4px;">Threshold: ${lowerStr} - ${upperStr}</div>`;
          }
          if (thresholds.target !== null) {
            thresholdText += `<div style="color: ${TARGET_LINE_COLOR}; font-size: 11px; margin-top: 2px; font-weight: bold;">Target: ${formatMetricValue(thresholds.target, valueType)}</div>`;
          }

          const uomText = unitOfMeasure
            ? `<div style="color: #666; font-size: 11px; margin-top: 4px;">Unit: ${unitOfMeasure}</div>`
            : '';

          return `
            <div style="padding: 8px;">
              <div style="font-weight: bold; margin-bottom: 4px;">${date}</div>
              <div style="color: ${violated ? '#f44336' : '#4caf50'};">
                Value: ${formatMetricValue(value, valueType)}
                ${violated ? ' ⚠️ Violated' : ''}
              </div>
              ${uomText}
              ${thresholdText}
            </div>
          `;
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '12%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: dates,
        axisLabel: { rotate: 45, fontSize: 10 },
        name: 'Date',
        nameLocation: 'middle',
        nameGap: 50,
      },
      yAxis: {
        type: 'value',
        name: getYAxisLabel(valueType),
        nameLocation: 'middle',
        nameGap: 60,
        min: valueRange > 0 ? minValue - valueRange * 0.1 : undefined,
        max: valueRange > 0 ? maxValue + valueRange * 0.1 : undefined,
        axisLabel: {
          formatter: (value: number) => formatMetricValue(value, valueType),
        },
      },
      series: [
        {
          name: metricName,
          type: chartType,
          data: values,
          smooth: chartType === 'line',
          symbol: chartType === 'line' ? 'circle' : undefined,
          symbolSize: chartType === 'line' ? 8 : undefined,
          lineStyle: chartType === 'line' ? { width: 3, color: '#1976d2' } : undefined,
          itemStyle: {
            color: (params: any) => {
              return violations[params.dataIndex] ? '#f44336' : '#4caf50';
            },
          },
          areaStyle:
            chartType === 'line'
              ? {
                  color: {
                    type: 'linear',
                    x: 0,
                    y: 0,
                    x2: 0,
                    y2: 1,
                    colorStops: [
                      { offset: 0, color: 'rgba(25, 118, 210, 0.3)' },
                      { offset: 1, color: 'rgba(25, 118, 210, 0.05)' },
                    ],
                  },
                }
              : undefined,
          markLine:
            markLineData.length > 0
              ? { data: markLineData, silent: true, symbol: 'none' }
              : undefined,
        },
      ],
      dataZoom: [
        { type: 'inside', start: 0, end: 100 },
        { start: 0, end: 100 },
      ],
    };
  }, [sortedData, chartType, metricName, valueType, unitOfMeasure, thresholds, showThresholds, aggregateLabel]);

  const handleChartTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newChartType: 'line' | 'bar' | null
  ) => {
    if (newChartType !== null) {
      setChartType(newChartType);
    }
  };

  if (data.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: height,
          bgcolor: 'grey.50',
          borderRadius: 2,
        }}
      >
        <Typography color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Metadata chips */}
      {showMetadata && (
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {reviewEntityName && (
            <Chip
              label={`Entity: ${reviewEntityName}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          {metricType && (
            <Chip
              label={`Dimension: ${metricType}`}
              size="small"
              color="secondary"
              variant="outlined"
            />
          )}
          {metricIdentifierName && (
            <Chip
              label={`Identifier: ${metricIdentifierName}`}
              size="small"
              variant="outlined"
            />
          )}
          {isAggregate && (
            <Chip label="Aggregated" size="small" color="info" variant="filled" />
          )}
          <Chip label={`Data Points: ${data.length}`} size="small" variant="outlined" />
        </Box>
      )}

      {/* Chart type toggle */}
      {showChartTypeToggle && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Chart Type:
          </Typography>
          <ToggleButtonGroup
            value={chartType}
            exclusive
            onChange={handleChartTypeChange}
            size="small"
          >
            <ToggleButton value="line" sx={{ textTransform: 'none' }}>
              <TrendingUpIcon sx={{ mr: 1, fontSize: 18 }} />
              Line
            </ToggleButton>
            <ToggleButton value="bar" sx={{ textTransform: 'none' }}>
              <BarChartIcon sx={{ mr: 1, fontSize: 18 }} />
              Bar
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Chart */}
      <ReactECharts
        option={chartOption}
        style={{ height: typeof height === 'number' ? `${height}px` : height, width: '100%' }}
        opts={{ renderer: 'svg' }}
        notMerge={true}
      />

      {/* Summary statistics */}
      {showSummary && stats && (
        <Paper elevation={0} sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Summary
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Latest Value
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatMetricValue(stats.latest, valueType)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Min
              </Typography>
              <Typography variant="body2">
                {formatMetricValue(stats.min, valueType)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Max
              </Typography>
              <Typography variant="body2">
                {formatMetricValue(stats.max, valueType)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Avg
              </Typography>
              <Typography variant="body2">
                {formatMetricValue(stats.avg, valueType)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Violations
              </Typography>
              <Typography
                variant="body2"
                color={stats.violations > 0 ? 'error.main' : 'success.main'}
              >
                {stats.violations} / {stats.total}
              </Typography>
            </Box>
            {(thresholds.lower !== null || thresholds.upper !== null || thresholds.target !== null) && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Threshold Range
                </Typography>
                <Typography variant="body2">
                  {thresholds.lower !== null
                    ? formatMetricValue(thresholds.lower, valueType)
                    : '-'}
                  {' — '}
                  {thresholds.upper !== null
                    ? formatMetricValue(thresholds.upper, valueType)
                    : '-'}
                </Typography>
                {thresholds.target !== null && (
                  <Typography variant="body2" sx={{ color: TARGET_LINE_COLOR, fontWeight: 600 }}>
                    Target: {formatMetricValue(thresholds.target, valueType)}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default MetricsChart;
